import Icon from '@atoms/Icon/Icon';
import { IAccountDropdown } from '@models/IAccountDropdown';
import { IMainNavigationItem } from '@models/IHeader';
import { IImage } from '@models/IImage';
import ILanguageSelector from '@models/ILanguageSelector';
import { ILink } from '@models/ILink';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction, useState } from 'react';
import MobileTopLevel from './MobileTopLevel';

interface Props {
  readonly accountLink: ILink;
  readonly accountsEnabled: boolean;
  readonly accountDropdownNavigation: IAccountDropdown;
  readonly backLabel: string;
  readonly language: string;
  readonly languageActiveIndex: number;
  readonly languageSelector?: ILanguageSelector;
  readonly logo: IImage;
  readonly mainNavigation: IMainNavigationItem[];
  readonly serviceAccountLink?: ILink;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly showAccountIcon?: boolean;
}

const MobileSubNav = ({
  accountLink,
  accountsEnabled,
  accountDropdownNavigation,
  backLabel,
  language,
  languageActiveIndex,
  languageSelector,
  logo,
  mainNavigation,
  serviceAccountLink,
  setIsOpen,
  showAccountIcon,
}: Props) => {
  const [isOpenSecondLevel, setIsOpenSecondLevel] = useState(false);
  const {
    closeMobileNavigationAriaLabel,
    backToFirstLevelNavigationAriaLabel,
  } = useAriaLabels();

  const topLevelProps = {
    accountLink,
    accountsEnabled,
    accountDropdownNavigation,
    mainNavigation,
    logo,
    isOpenSecondLevel,
    setIsOpenSecondLevel,
    languageActiveIndex,
    languageSelector,
    language,
    serviceAccountLink,
    showAccountIcon,
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{
        delay: 0.15,
        duration: 1.1,
        ease: [0.74, 0, 0.19, 1.02],
      }}
      className="fixed top-0 left-0 z-max flex h-full w-screen flex-col overflow-hidden bg-white"
      data-testid="CT-01-Header-Mobile-Sub-Nav"
    >
      <div className="relative flex flex-row items-center px-5 pt-8 pb-11 md:pr-8 md:pl-8 lgm:pl-32">
        <AnimatePresence>
          {isOpenSecondLevel && (
            <motion.button
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{
                delay: 0.15,
                duration: 1.1,
                ease: [0.74, 0, 0.19, 1.02],
              }}
              className="absolute flex flex-row"
              onClick={() => setIsOpenSecondLevel(false)}
              onKeyDown={() => setIsOpenSecondLevel(false)}
              aria-label={backToFirstLevelNavigationAriaLabel}
              data-testid="CT-01-Header-Mobile-Sub-Nav-Back-Button"
            >
              <div className="mr-4 flex h-7 w-7 rotate-180 items-center justify-center rounded-full bg-yellow">
                <Icon name="east" size={IconSizeEnum.sm} />
              </div>
              <p
                className="flex items-center"
                data-testid="CT-01-Header-Mobile-Sub-Nav-Back-Button-Label"
              >
                {backLabel}
              </p>
            </motion.button>
          )}
        </AnimatePresence>
        <button
          className="ml-auto flex justify-end"
          onClick={() => setIsOpen((oldState) => !oldState)}
          onKeyDown={() => setIsOpen((oldState) => !oldState)}
          aria-label={closeMobileNavigationAriaLabel}
        >
          <Icon name="close" size={IconSizeEnum.lg} />
        </button>
      </div>
      <div className="grow overflow-scroll bg-white">
        <MobileTopLevel {...topLevelProps} />
      </div>
    </motion.div>
  );
};

export default MobileSubNav;
